import React, { useEffect, useState, useRef } from "react";
import { apiFetch } from "../api";
import { useNavigate } from "react-router-dom";
import EditUserProfileModal from "./EditUserProfileModal";
import EditBureauProfileModal from "./EditBureauProfileModal";
import { useLanguage } from "../context/LanguageContext";
import { useLoading } from "../contexts/LoadingContext";
import { buildNavItems, useUnreadCounts } from "./Sidebar";
import { avatarUrl } from "../utils/cloudinary";

const NavBar = ({ onEditProfile }) => {
  const [bureau, setBureau] = useState(null);
  const [publicUser, setPublicUser] = useState(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const profileDropdownRef = useRef(null);
  const { setLoading, setGlobalLoadingMessage } = useLoading();
  const navigate = useNavigate();
  const { t, isRTL } = useLanguage();

  // Mobile menu mirrors the desktop Sidebar by reusing the same builder
  // and unread-count hook — labels, paths, icons, and badges can't drift.
  // Polling is skipped when nobody is signed in (login/register pages).
  const isPublicUser = !!publicUser;
  const isAuthed = !!publicUser || !!bureau;
  const { unreadInterests, unreadAssociations } = useUnreadCounts({
    isPublicUser,
    enabled: isAuthed,
  });
  const navItems = isAuthed
    ? buildNavItems({ isPublicUser, unreadInterests, unreadAssociations })
    : [];

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        // Check if this is a public user or bureau admin
        const publicToken = localStorage.getItem("publicUserToken");

        if (publicToken) {
          // Fetch public user profile
          const data = await apiFetch("/public/auth/profile");
          setPublicUser(data);
        } else {
          // Fetch bureau profile
          const data = await apiFetch("/auth/profile");
          setBureau(data);
        }
      } catch (err) {
        console.error("Failed to load profile:", err);
      }
    };
    fetchProfile();
  }, []);

  // Bell-icon unread count. Polls the role-appropriate endpoint every 60s
  // while the NavBar is mounted. Failures (no session yet, network blip)
  // are swallowed — the bell just stays at the previous value.
  //
  // The Notifications page dispatches custom events on mark-as-read so
  // the bell updates instantly instead of waiting up to 60s for the next
  // poll. See pages/Notifications.jsx for the event names.
  const [unread, setUnread] = useState(0);
  useEffect(() => {
    let cancelled = false;
    const endpoint = localStorage.getItem("publicUserToken")
      ? "/public/notifications/unread-count"
      : "/notifications/unread-count";
    const tick = () =>
      apiFetch(endpoint)
        .then((r) => {
          if (!cancelled) setUnread(r?.count || 0);
        })
        .catch(() => {});
    tick();
    const id = setInterval(tick, 60000);

    const onCleared = () => setUnread(0);
    const onReadOne = () =>
      setUnread((n) => (n > 0 ? n - 1 : 0));
    window.addEventListener("notifications:cleared", onCleared);
    window.addEventListener("notifications:read-one", onReadOne);

    return () => {
      cancelled = true;
      clearInterval(id);
      window.removeEventListener("notifications:cleared", onCleared);
      window.removeEventListener("notifications:read-one", onReadOne);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target)) {
        setIsProfileDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = async () => {
    try {
      setLoading(true);
      setGlobalLoadingMessage("Signing out...");
      
      const publicToken = localStorage.getItem("publicUserToken");
      if (publicToken) {
        // Public user logout
        localStorage.removeItem("publicUserToken");
        navigate("/");
      } else {
        // Bureau logout
        await apiFetch("/auth/logout", { method: "POST" });
        try { localStorage.removeItem("bureauActive"); } catch { /* */ }
        navigate("/bureau");
      }
    } catch (err) {
      console.error("Logout failed:", err);
    } finally {
      setLoading(false);
      setGlobalLoadingMessage("");
    }
  };

  const handleEditProfileSaved = (msg) => {
    setIsEditOpen(false);
    setIsMenuOpen(false);
    setIsProfileDropdownOpen(false);
    // Refresh public user data
    if (publicUser) {
      apiFetch("/public/auth/profile")
        .then((data) => setPublicUser(data))
        .catch((err) => console.error("Failed to refresh profile:", err));
    }
    if (bureau) {
      apiFetch("/auth/profile")
        .then((data) => setBureau(data))
        .catch((err) => console.error("Failed to refresh bureau profile:", err));
    }
    if (onEditProfile) {
      onEditProfile(msg);
    }
  };

  const handleMenuToggle = () => {
    setIsMenuOpen((prev) => !prev);
  };

  // Body-scroll lock driven entirely off the menu state so it can never get
  // stuck on `hidden` when the menu is closed via a path that doesn't go
  // through handleMenuToggle (clicking a menu item, the backdrop, the X
  // button, or navigating away while the menu was open).
  useEffect(() => {
    if (isMenuOpen) {
      const previous = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = previous || "";
      };
    }
    // Closed state — be defensive and clear the lock in case anything else
    // left it set.
    document.body.style.overflow = "";
    return undefined;
  }, [isMenuOpen]);

  const displayName = publicUser ? publicUser.fullName : (bureau ? bureau.name : "Loading...");
  const displayImage = bureau?.image || bureau?.logo || bureau?.photo;
  // When a bureau is signed in, surface the operator currently using this
  // session. /auth/profile attaches `member: {fullName, email, role}` so a
  // shared workstation makes it obvious who is signed in.
  const member = bureau?.member;

  return (
    <>
      <nav className={`sticky top-0 z-40 grid grid-cols-3 items-center border-b border-[var(--color-border)] bg-[var(--color-surface-muted)] px-4 py-3 shadow-md backdrop-blur ${isRTL ? 'rtl' : ''}`}>
        {/* Hamburger Menu — only on mobile; md+ uses the persistent sidebar */}
        <div className={`md:hidden col-start-1 ${isRTL ? 'justify-self-end' : 'justify-self-start'}`}>
          <button
            type="button"
            onClick={handleMenuToggle}
            className="rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm font-semibold text-[var(--color-text)] hover:bg-[var(--color-surface-hover)] transition"
            aria-expanded={isMenuOpen}
            title={isMenuOpen ? 'Close menu' : 'Open menu'}
          >
            {isMenuOpen ? '✕' : '☰'}
          </button>
        </div>

        {/* Display name + profile avatar — right side. Language + theme
            switchers moved into the sidebar's Settings modal. */}
        <div className={`col-start-3 flex items-center gap-2 ${isRTL ? 'justify-self-start flex-row-reverse' : 'justify-self-end'}`}>
          {/* Notifications bell with unread badge. Links to the shared
              /notifications inbox for both roles. */}
          <button
            type="button"
            onClick={() => navigate("/notifications")}
            title={unread > 0 ? `${unread} unread notifications` : "Notifications"}
            className="relative inline-flex items-center justify-center w-10 h-10 rounded-full border border-[var(--color-border)] hover:bg-[var(--color-surface-hover)] transition"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="w-5 h-5 text-[var(--color-text)]"
              aria-hidden="true"
            >
              <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
              <path d="M13.73 21a2 2 0 0 1-3.46 0" />
            </svg>
            {unread > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[1.1rem] h-[1.1rem] px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold leading-[1.1rem] text-center">
                {unread > 9 ? "9+" : unread}
              </span>
            )}
          </button>

          {/* Display name — sits just to the left of the profile avatar.
              For bureau sessions we stack the bureau name above a small
              "member · role" line so a shared workstation makes the signed-in
              operator obvious. */}
          <div className="hidden sm:flex flex-col items-end leading-tight max-w-[14rem]">
            <span className="text-sm font-semibold text-[var(--color-text-strong)] truncate">
              {displayName}
            </span>
            {member?.fullName && (
              <span className="text-[10px] text-[var(--color-text-muted)] truncate">
                {member.fullName}
                {member.role && (
                  <span className="ml-1 uppercase tracking-wider">
                    · {member.role}
                  </span>
                )}
              </span>
            )}
          </div>

        {/* Profile Dropdown - Right Side */}
        <div className="relative" ref={profileDropdownRef}>
          <button
            type="button"
            onClick={() => setIsProfileDropdownOpen((prev) => !prev)}
            className="w-10 h-10 rounded-full overflow-hidden bg-[var(--color-border)] flex items-center justify-center flex-shrink-0 cursor-pointer border border-[var(--color-border)] hover:border-[var(--color-primary)] transition"
            title="Profile menu"
          >
            {displayImage && !imageError ? (
              <img
                // Render at 40px CSS; helper requests 80px@2x from Cloudinary
                // with f_auto,q_auto + face-aware square crop. Non-Cloudinary
                // URLs (data: previews, placeholders) pass through unchanged.
                src={avatarUrl(displayImage, 40)}
                alt={`${displayName} Profile`}
                className="w-full h-full object-cover object-center"
                onError={() => setImageError(true)}
              />
            ) : (
              <span className="text-sm font-semibold text-[var(--color-text-strong)]">
                {displayName ? displayName.charAt(0).toUpperCase() : 'U'}
              </span>
            )}
          </button>

          {/* Profile Dropdown Menu */}
          {isProfileDropdownOpen && (
            <div className={`absolute top-full mt-2 ${isRTL ? 'left-0' : 'right-0'} w-48 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] shadow-lg`}>
              <div className="space-y-1 p-2">
                {(publicUser || bureau) && (
                  <button
                    onClick={() => {
                      setIsEditOpen(true);
                      setIsProfileDropdownOpen(false);
                    }}
                    className="w-full flex items-center gap-2 text-left rounded-lg px-4 py-2 text-sm font-medium text-[var(--color-text)] hover:bg-[var(--color-surface-hover)] transition"
                  >
                    {/* Pencil icon */}
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 shrink-0">
                      <path d="M12 20h9" />
                      <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
                    </svg>
                    <span>{t('edit')} {t('profile') || 'Profile'}</span>
                  </button>
                )}
                <hr className="my-1 border-[var(--color-border)]" />
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-2 text-left rounded-lg px-4 py-2 text-sm font-medium text-red-500 hover:bg-red-600 hover:text-white transition-colors"
                >
                  {/* Log-out icon */}
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 shrink-0">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                    <polyline points="16 17 21 12 16 7" />
                    <line x1="21" y1="12" x2="9" y2="12" />
                  </svg>
                  <span>{t('logout')}</span>
                </button>
              </div>
            </div>
          )}
        </div>
        </div>
      </nav>

      {/* Mobile Menu Backdrop — z-30 so it sits above sticky page headers (z-20). */}
      {isMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30"
          onClick={() => setIsMenuOpen(false)}
        />
      )}

      {/* Mobile Menu - Full Screen Overlay — z-50 so it covers everything,
          including the NavBar (z-40) and any sticky page header. */}
      {isMenuOpen && (
        <div className="fixed inset-0 z-50 flex">
          <div className="w-full bg-[var(--color-surface)] shadow-lg overflow-y-auto">
            <div className="p-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-[var(--color-text-strong)]">Menu</h2>
                <button
                  onClick={() => setIsMenuOpen(false)}
                  className="p-2 rounded-lg hover:bg-[var(--color-surface-hover)] transition"
                >
                  ✕
                </button>
              </div>
              
              {/* Mobile nav — rendered from the same buildNavItems() that
                  feeds the desktop Sidebar, so labels/paths/icons/badges
                  can never drift between the two surfaces. */}
              <nav
                className={`flex flex-col gap-1 ${isRTL ? 'flex-col-reverse' : ''}`}
                aria-label="Primary navigation"
              >
                <h3 className="text-xs font-semibold text-[var(--color-text-muted)] uppercase tracking-wider mb-1">
                  {isPublicUser ? "Browse" : "Bureau"}
                </h3>
                {navItems.map(({ label, path, Icon, badge }) => (
                  <button
                    key={path}
                    onClick={() => {
                      navigate(path);
                      setIsMenuOpen(false);
                    }}
                    className={`flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-[var(--color-text)] hover:bg-[var(--color-surface-hover)] hover:text-[var(--color-on-primary)] rounded transition ${
                      isRTL ? 'flex-row-reverse text-right' : 'text-left'
                    }`}
                  >
                    <Icon className="w-5 h-5 shrink-0" />
                    <span className="flex-1 truncate">{label}</span>
                    {badge > 0 && (
                      <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 text-[10px] font-semibold rounded-full bg-[var(--color-primary)] text-[var(--color-on-primary)]">
                        {badge > 99 ? "99+" : badge}
                      </span>
                    )}
                  </button>
                ))}
              </nav>
            </div>
          </div>
        </div>
      )}

      {publicUser && (
        <EditUserProfileModal
          isOpen={isEditOpen}
          onClose={() => setIsEditOpen(false)}
          user={publicUser}
          onSaved={handleEditProfileSaved}
        />
      )}
      {bureau && (
        <EditBureauProfileModal
          isOpen={isEditOpen}
          onClose={() => setIsEditOpen(false)}
          bureau={bureau}
          onSaved={handleEditProfileSaved}
        />
      )}
    </>
  );
};

export default NavBar;
